import React from "react";
import { makeStyles } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  CircularProgress,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { setTake, setPage } from "../Filtros/actions";
import { novedadesPath, paths } from "~constants/referenceNames";
import TableLoading from "~styled/tableLoading/TableLoading";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Filtros from "../Filtros/Filtros";
import Pagination from "~styled/pagination/Pagination";
import { type OptionKV } from "~styled/selector/Selector";
import { paginationOptions } from "~constants/pagination";
import ColorLabelNegocio from "~styled/colorLabelNegocio/ColorLabelNegocio";
import NumberFormat from "react-number-format";
import { format, isValid } from "date-fns";
import { es } from "date-fns/esm/locale";
import { GetNovedadResponse } from "~api/types";
import { TakeValue } from "../../types";

const useStyles = makeStyles((theme: any) => ({
  root: {
    width: "100%",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 3px 6px 0 rgba(0, 0, 0, 0.16)",
    backgroundColor: theme.colours.white,
  },
  tableContainer: {
    width: "100%",
    backgroundColor: theme.colours.white,
    overflow: "auto",
    minHeight: 500,
  },
  progress: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  table: {
    fontSize: 16,
    color: "#405A84",
  },
  sinCotizaciones: {
    color: theme.colours.brownGrey2,
    lineHeight: "13px",
    fontSize: "2vh",
  },
  iconButton: {
    color: theme.colours.verdeMgBroker,
    padding: 0,
  },
  iconPdfButton: {
    color: "#b00020",
    padding: 0,
  },
  tableCell: {
    paddingLeft: 20,
    paddingRight: 0,
    fontSize: "12px",
    //fontWeight: "bold",
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.33",
    letterSpacing: "normal",
    textAlign: "left",
    //color: theme.colours.violeta
  },
  tableCellCenter: {
    paddingLeft: 20,
    paddingRight: 0,
    fontSize: "12px",
    //fontWeight: "bold",
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.33",
    letterSpacing: "normal",
    textAlign: "center",
    alignItems: "center",
    //color: theme.colours.violeta
  },
  tableCellHeader: {
    fontSize: "small",
    paddingRight: 0,
    fontWeight: "bold",
    fontStyle: "normal",
    fontStretch: "normal",
    lineHeight: "1.33",
    letterSpacing: "normal",
    textAlign: "left",
    color: theme.colours.violeta,
  },
  dash: { display: "flex", alignItems: "center", justifyContent: "center" },
}));

const emptyListText = "No hay resultados.";

interface SliceListadoNovedades {
  take: TakeValue;
  page: number;
  novedades: GetNovedadResponse[] | null;
  totalRegistros: number;
  isLoading: boolean;
}

const useReduxSelector = () =>
  useSelector<any, SliceListadoNovedades>((state) => ({
    take: state.filtrosNovedadesReducer.take,
    page: state.filtrosNovedadesReducer.page,

    novedades: state.novedadesReducer.novedades,
    totalRegistros: state.novedadesReducer.totalRegistros,
    isLoading: state.novedadesReducer.isLoading,
  }));

// El backend puede mandar fechas vacías o inválidas: devolver null en vez de
// dejar que date-fns lance y desmonte todo el listado.
const formatFecha = (value?: string | null): string | null => {
  if (!value) return null;

  const fecha = new Date(value);
  if (!isValid(fecha)) return null;

  return format(fecha, "dd'/'MM'/'yyyy", { locale: es });
};

const formatVigencia = (
  desde?: string | null,
  hasta?: string | null
): string | null => {
  const fechaDesde = formatFecha(desde);
  const fechaHasta = formatFecha(hasta);
  if (!fechaDesde && !fechaHasta) return null;

  return `${fechaDesde ?? "—"} hasta ${fechaHasta ?? "—"}`;
};

const ListadoNovedades: React.FC = () => {
  const { page, take, novedades, totalRegistros, isLoading } =
    useReduxSelector();
  const dispatch = useDispatch();
  const empty = totalRegistros === 0;

  const classes = useStyles();

  const handleChangePage = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (value: TakeValue) => {
    dispatch(setTake(value));
  };

  const getValueParsed = (value: React.ReactNode) => {
    if (!value) return <div className={classes.dash}>&mdash;</div>;

    return value;
  };

  const paginationOptionsStr = React.useMemo(
    () =>
      paginationOptions.map((option) => ({
        label: String(option.label),
        value: option.value,
      })),
    []
  );

  return (
    <>
      <Paper className={classes.root}>
        <div className={classes.tableContainer}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Filtros />
          </div>
          <Divider></Divider>
          {isLoading && <TableLoading length={5} />}
          {!isLoading && (
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Negocio"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Tipo"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Compañia"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"N° Póliza"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Dominio"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Descargada"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Vigencia"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Premio"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"DNI"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Titular"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Documentación"}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography
                      color="textSecondary"
                      className={classes.tableCellHeader}
                    >
                      {"Detalle"}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.table}>
                {isLoading && (
                  <div className={classes.progress}>
                    <CircularProgress size={60} />
                  </div>
                )}
                {!isLoading &&
                  !empty &&
                  novedades &&
                  novedades.map((novedad) => (
                    <TableRow
                      key={"table-novedades-" + novedad.id}
                      style={{ textDecoration: "none" }}
                    >
                      <TableCell className={classes.tableCell}>
                        <ColorLabelNegocio
                          negocio={novedad.negocio}
                          abbreviated
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getValueParsed(novedad.tipoNovedad)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getValueParsed(novedad.compania)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getValueParsed(novedad.numeroPoliza)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getValueParsed(novedad.dominio)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getValueParsed(
                          formatFecha(novedad.fechaCreacionNovedad)
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getValueParsed(
                          formatVigencia(
                            novedad.polizaVigenciaDesde,
                            novedad.polizaVigenciaHasta
                          )
                        )}
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {
                          <NumberFormat
                            value={novedad.premioTotal}
                            displayType={"text"}
                            thousandSeparator={"."}
                            decimalSeparator={","}
                            isNumericString
                            prefix="$"
                            decimalScale={2}
                          />
                        }
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {getValueParsed(novedad.numeroDocumentoAsegurado)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {getValueParsed(novedad.nombreCompletoAsegurado)}
                      </TableCell>
                      <TableCell className={classes.tableCellCenter}>
                        <Tooltip title={"Constancia de Póliza"}>
                          <a
                            href={novedad.urlPoliza}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              disabled={!novedad.urlPoliza}
                              className={classes.iconPdfButton}
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </a>
                        </Tooltip>
                        <Tooltip title={"Tarjeta circulación"}>
                          <a
                            href={novedad.urlTarjeta}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              disabled={!novedad.urlTarjeta}
                              className={classes.iconPdfButton}
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </a>
                        </Tooltip>
                        <Tooltip title={"Cédula MERCOSUR"}>
                          <a
                            href={novedad.urlCedulaMercosur}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              disabled={!novedad.urlCedulaMercosur}
                              className={classes.iconPdfButton}
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </a>
                        </Tooltip>
                        <Tooltip title={"Cuponera"}>
                          <a
                            href={novedad.urlCuponera}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              disabled={!novedad.urlCuponera}
                              className={classes.iconPdfButton}
                            >
                              <PictureAsPdfIcon />
                            </IconButton>
                          </a>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={classes.tableCellCenter}>
                        <Tooltip title={"Detalles de la novedad " + novedad.id}>
                          <IconButton
                            className={classes.iconButton}
                            component={Link}
                            to={paths[novedadesPath] + "/" + novedad.id}
                          >
                            <InfoOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
          {!isLoading && empty ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                paddingTop: "2vh",
                paddingBottom: "2vh",
              }}
            >
              <Typography className={classes.sinCotizaciones}>
                {emptyListText}
              </Typography>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                paddingTop: "2vh",
                paddingBottom: "2vh",
                width: "100%",
              }}
            >
              {!isLoading && (
                <Pagination<TakeValue>
                  rowsPerPage={take}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  totalRegistros={totalRegistros}
                  options={paginationOptionsStr as OptionKV<TakeValue>[]}
                />
              )}
            </div>
          )}
        </div>
      </Paper>
    </>
  );
};

export default ListadoNovedades;
